import http from './http';
import {PROJECT_BASE} from './paths';
import {withPerm} from './perm';

const BASE_URL = PROJECT_BASE;

// 프로젝트 단어 목록 조회
export const getProjectWordList = withPerm(async (pjtId) => {
  const response = await http.get(`${BASE_URL}/${pjtId}/word`);
  return response.data;
}, 'GET', `${BASE_URL}/{pjtId}/word`);

// 프로젝트 단어 생성
export const createProjectWord = withPerm(async (pjtId, payload) => {
  const response = await http.post(`${BASE_URL}/${pjtId}/word`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}, 'POST', `${BASE_URL}/{pjtId}/word`);

// LLM 기반 단어 제안(영문명/설명/약어)
export const suggestWord = withPerm(async (pjtId, payload) => {
  const response = await http.post(`${BASE_URL}/${pjtId}/word/suggest`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}, 'POST', `${BASE_URL}/{pjtId}/word/suggest`);

// 프로젝트 단어 일괄 생성 (엑셀 업로드)
export const createProjectWordBatch = withPerm(async (pjtId, list) => {
  const response = await http.post(`${BASE_URL}/${pjtId}/word/batch`, list, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}, 'POST', `${BASE_URL}/{pjtId}/word/batch`);

// 프로젝트 단어 수정 (UUID 또는 내부 PK를 pjtWordId 로 가정)
export const updateProjectWord = withPerm(async (pjtId, pjtWordId, payload) => {
  const response = await http.patch(`${BASE_URL}/${pjtId}/word/${pjtWordId}`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}, 'PATCH', `${BASE_URL}/{pjtId}/word/{pjtWordId}`);

// 프로젝트 단어 업서트(존재시 UPDATE, 없으면 INSERT) 배치 (서버 native upsert)
export const upsertProjectWordBatch = withPerm(async (pjtId, list = []) => {
  const response = await http.post(`${BASE_URL}/${pjtId}/word/upsert-batch`, list, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}, 'POST', `${BASE_URL}/{pjtId}/word/upsert-batch`);

// 프로젝트 단어 단건 삭제
export const deleteProjectWord = withPerm(async (pjtId, pjtWordId) => {
  const response = await http.delete(`${BASE_URL}/${pjtId}/word/${pjtWordId}`);
  return response.data;
}, 'DELETE', `${BASE_URL}/{pjtId}/word/{pjtWordId}`);

// 일괄 삭제(헬퍼): 선택 삭제는 단건 삭제 병렬 처리 유지
export const deleteProjectWordBatch = async (pjtId, idList = []) => {
  if (!Array.isArray(idList)) throw new Error('idList must be an array');
  const tasks = idList.map((id) => deleteProjectWord(pjtId, id));
  return Promise.all(tasks);
};

// 권한 메타 보강: 선택 삭제 헬퍼 버튼 비활성 문제 해결
if (!deleteProjectWordBatch._perm && deleteProjectWord?._perm) {
  deleteProjectWordBatch._perm = { ...deleteProjectWord._perm }; // 단건 삭제 권한과 동일 패턴
}

/**
 * 안전 업서트: 서버 upsert-batch 미지원(404/405) 혹은 실패 시 클라이언트 폴백 수행
 * 규칙:
 *  - 다운로드 양식의 uuid 컬럼(필드명 'uuid' 가정)을 그대로 유지해 업로드
 *  - 동일 uuid 존재 -> updateProjectWord 호출
 *  - uuid 없거나 신규 -> 일괄 create
 * @param {string|number} pjtId
 * @param {Array<Object>} rows - 엑셀에서 파싱된 행 목록
 * @param {Object} [options]
 * @param {boolean} [options.forceClient=false] true 시 서버 upsert 시도 없이 바로 폴백 로직
 * @returns {Promise<{updated:number, created:number, errors:Object[], serverUsed:boolean}>}
 */
export async function safeUpsertProjectWordBatch(pjtId, rows = [], options = {}) {
  const { forceClient = false } = options;
  if (!Array.isArray(rows)) throw new TypeError('rows must be an array');
  if (rows.length === 0) return { updated: 0, created: 0, errors: [], serverUsed: false };

  // 1) 서버 native upsert 먼저 시도 (옵션으로 skip 가능)
  if (!forceClient) {
    try {
      await upsertProjectWordBatch(pjtId, rows);
      return { updated: rows.length, created: 0, errors: [], serverUsed: true };
    } catch (e) {
      if (!(e?.response?.status && [404, 405, 501].includes(e.response.status))) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[safeUpsertProjectWordBatch] native upsert failed, fallback:', e?.message);
        }
      }
      // 계속 진행하여 폴백
    }
  }

  // 2) 클라이언트 폴백: 기존 목록 조회
  let existingListRaw;
  try {
    existingListRaw = await getProjectWordList(pjtId);
  } catch (e) {
    throw new Error('기존 목록 조회 실패: ' + (e?.message || 'unknown'));
  }

  // 허용 가능한 형태:
  //  a) Array
  //  b) { data: Array }
  //  c) { list: Array }
  let existingList;
  if (Array.isArray(existingListRaw)) {
    existingList = existingListRaw;
  } else if (Array.isArray(existingListRaw?.data)) {
    existingList = existingListRaw.data;
  } else if (Array.isArray(existingListRaw?.list)) {
    existingList = existingListRaw.list;
  } else {
    existingList = [];
  }

  const existingMap = new Map();
  if (existingList.length && typeof existingList.forEach === 'function') {
    existingList.forEach(item => {
      const key = item?.uuid || item?.id || item?.pjtWordId; // 가능한 키 후보
      if (key) existingMap.set(String(key), item);
    });
  }

  const updates = [];
  const creates = [];

  rows.forEach(row => {
    const key = row.uuid || row.id || row.pjtWordId;
    if (key && existingMap.has(String(key))) {
      updates.push(row);
    } else {
      creates.push(row);
    }
  });

  const errors = [];

  // 3) 업데이트 (순차 또는 소량 병렬) - 지나친 동시성 회피 (최대 5 동시)
  const concurrency = 5;
  let index = 0;
  let updatedCount = 0;

  async function runNext() {
    if (index >= updates.length) return;
    const row = updates[index++];
    const key = row.uuid || row.id || row.pjtWordId;
    try {
      await updateProjectWord(pjtId, key, row);
      updatedCount++;
    } catch (e) {
      errors.push({ type: 'update', key, message: e?.message || 'update error' });
    }
    if (index < updates.length) await runNext();
  }

  const starters = [];
  const startCount = Math.min(concurrency, updates.length);
  for (let i = 0; i < startCount; i++) starters.push(runNext());
  await Promise.all(starters);

  // 4) 생성 - 일괄 호출 (너무 많으면 chunk)
  let createdCount = 0;
  if (creates.length > 0) {
    const CHUNK = 500; // 서버 부담 방지
    for (let i = 0; i < creates.length; i += CHUNK) {
      const slice = creates.slice(i, i + CHUNK);
      try {
        await createProjectWordBatch(pjtId, slice);
        createdCount += slice.length;
      } catch (e) {
        errors.push({ type: 'createBatch', indexStart: i, size: slice.length, message: e?.message || 'create batch error' });
      }
    }
  }

  return { updated: updatedCount, created: createdCount, errors, serverUsed: false };
}
